import { Component, Fragment } from 'react'
import { Collapse, FormControl, FormHelperText, inputBaseClasses, InputLabel, MenuItem, Select, selectClasses, styled } from '@mui/material'
import { mergeObjects } from '../utils/helpers'
import { getErrorMessage } from '../form/helpers'
import { HelpOutlinePulseIcon, RichTooltip } from '../components/rich-tooltip'
import type { ComponentType } from 'react'
import type { SelectProps } from '@mui/material'
import type { IFormInputBase } from '../form/types'
import type { IRichTooltipPanelConfig } from '../components/rich-tooltip'

export interface ISelectSimpleOption<T extends string = string> {
  name: string
  value: T
}

export interface ISelectSimpleSlots {
  selectProps?: Omit<SelectProps, 'variant'>
}

export interface ISelectSimpleProps<T> extends Partial<IFormInputBase<T>> {
  options?: ISelectSimpleOption[]
  value?: any
  onChange?: (value: ISelectSimpleOption) => void
  slots?: ISelectSimpleSlots
  fullWidth?: boolean
  tooltip?: IRichTooltipPanelConfig
  endAdornment?: React.ReactNode
}

export interface ISelectSimpleState {
  value?: string
}

export interface ISelectSimpleParams extends ISelectSimpleSlots {
  options?: ISelectSimpleOption[]
  tooltip?: IRichTooltipPanelConfig
  endAdornment?: React.ReactNode
}

function createSelectSimple<T>(args?: ISelectSimpleParams): ComponentType<ISelectSimpleProps<T>> {
  class SelectSimple extends Component<ISelectSimpleProps<T>, ISelectSimpleState> {
    constructor(props: ISelectSimpleProps<T>) {
      super(props)
      this.state = { value: this.getDefaultValue()?.toString() }
    }

    shouldComponentUpdate(nextProps: Readonly<ISelectSimpleProps<T>>): boolean {
      const currentDValue = this.getDefaultValue()
      const nextDValue = this.getDefaultValue(nextProps)
      if (currentDValue !== nextDValue) {
        this.setState({ value: nextDValue?.toString() })
        return false
      }
      return true
    }

    mapProps = (): SelectProps => {
      const label = this.getLabel()
      const tfp: SelectProps = {
        id: this.props.name?.toString(),
        labelId: this.props.name?.toString() + label,
        name: this.props.name?.toString(),
        label: label,
        defaultValue: this.getDefaultValue(),
        value: this.state.value,
        onChange: (event) => {
          const value: string = event.target.value + ''
          this.setState({ value }, () => {
            if (!!this.props.name) {
              this.props.onBlur && this.props.onBlur(this.props.name)
            }
            const options = this.getOptions()
            const temp = options.find((x) => x.value?.toString() === value)
            if (!temp) return
            this.props.onChange && this.props.onChange(temp)
          })
        },
        disabled: this.props.disabled,
        fullWidth: this.props.fullWidth !== undefined ? this.props.fullWidth : true,
        endAdornment: this.props.endAdornment ?? args?.endAdornment ?? undefined
      }
      const selectProps = this.props.slots?.selectProps ?? args?.selectProps
      return mergeObjects({}, tfp, selectProps)
    }

    render() {
      const data = this.getOptions()
      const label = this.getLabel()
      const errorMessage = getErrorMessage(this.props.messageErrors, this.props.name)
      return (
        <Fragment>
          {!!this.props.disabled && <input hidden name={this.props.name?.toString()} defaultValue={this.getDefaultValue()} />}
          <SelectSimpleStyled className={this.getRootClasses()} fullWidth disabled={this.props.disabled} error={errorMessage.error}>
            <InputLabel id={this.props.name?.toString() + label}>{label}</InputLabel>
            <Select {...this.mapProps()}>
              {data.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
            <Collapse in={errorMessage.error}>
              <FormHelperText>{errorMessage.message}</FormHelperText>
            </Collapse>
            {this.renderTooltip()}
          </SelectSimpleStyled>
        </Fragment>
      )
    }

    renderTooltip = () => {
      const tooltip = this.props.tooltip || args?.tooltip
      if (!tooltip) return null
      return (
        <div className={selectSimpleClasses.tooltipWrap}>
          <RichTooltip panel={tooltip}>
            <HelpOutlinePulseIcon fontSize='small' />
          </RichTooltip>
        </div>
      )
    }

    getRootClasses = () => {
      const classes = [selectSimpleClasses.root]
      const tooltip = this.props.tooltip || args?.tooltip
      if (tooltip) classes.push(selectSimpleClasses.tooltip)
      if (this.props.endAdornment || args?.endAdornment) classes.push(selectSimpleClasses.endAdornment)
      const selectPropsSize = this.props.slots?.selectProps?.size ?? args?.selectProps?.size
      if (selectPropsSize === 'small') classes.push(selectSimpleClasses.small)
      return classes.join(' ')
    }

    getLabel = () => {
      if (!!this.props.label && typeof this.props.label === 'string') return this.props.label
      return this.props.name?.toString() ?? ''
    }

    getDefaultValue = (forceProps?: ISelectSimpleProps<T>) => {
      const currentProps = forceProps ?? this.props
      const { data, name } = currentProps
      const options = this.getOptions()
      return currentProps.defaultValue?.toString() ?? (!!data && !!name ? data[name] : undefined) ?? options[0]?.value
    }

    getOptions = (): ISelectSimpleOption[] => {
      return args?.options ?? this.props.options ?? []
    }
  }
  return SelectSimple
}
export default createSelectSimple

export const selectSimpleClasses = {
  root: 'DinoSelectSimple-root',
  tooltipWrap: 'DinoSelectSimple-tooltipWrap',
  // status classes
  small: 'DinoSelectSimple-small',
  tooltip: 'DinoSelectSimple-tooltip',
  endAdornment: 'DinoSelectSimple-endAdornment'
}
const classes = selectSimpleClasses // redefined for easy access

const SelectSimpleStyled = styled(FormControl)(({ theme }) => ({
  [`&.${classes.root}`]: { position: 'relative' },
  [`.${classes.tooltipWrap}`]: {
    position: 'absolute',
    top: theme.spacing(2.25),
    right: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  [`&.${classes.tooltip} .${selectClasses.icon}`]: { right: theme.spacing(4.5) },
  [`&.${classes.small} .${classes.tooltipWrap}`]: { top: theme.spacing(1.25) },
  [`&.${classes.endAdornment}`]: {
    [`.${inputBaseClasses.root}`]: { paddingRight: theme.spacing(1) },
    [`.${selectClasses.icon}`]: { right: theme.spacing(4.5) }
  }
}))
